/** Dialog 'Nova agenda' / 'Editar agenda' (Nome* + Cor) com exclusão confirmada. */
import { useState, type FormEvent, type ReactNode } from 'react';
import type { Calendar, CalendarBody } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog } from '@/components/ui/misc';
import { errorCode, errorMessage } from './agenda-queries';
import { useCreateCalendar, useDeleteCalendar, useUpdateCalendar } from './agenda-mutations';

const DEFAULT_COLOR = '#0CC4B6';

function CalendarForm({
  initial,
  pending,
  error,
  onSubmit,
  onCancel,
  footerLeft,
}: {
  initial?: Calendar;
  pending: boolean;
  error: unknown;
  onSubmit: (body: CalendarBody) => void;
  onCancel: () => void;
  footerLeft?: ReactNode;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? DEFAULT_COLOR);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), color });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="calendar-name">
          Nome <span className="text-destructive">*</span>
        </Label>
        <Input
          id="calendar-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="calendar-color">Cor</Label>
        <input
          id="calendar-color"
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="block h-9 w-full cursor-pointer rounded-md border border-input bg-transparent p-1 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
          aria-label="Cor da agenda"
        />
      </div>
      {error ? <p className="text-sm text-destructive">{errorMessage(error)}</p> : null}
      <div className="flex items-center justify-between gap-2 pt-1">
        <div>{footerLeft}</div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={pending || !name.trim()}>
            {pending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>
    </form>
  );
}

export function NewCalendarDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateCalendar();
  return (
    <Dialog open onClose={onClose} title="Nova agenda">
      <CalendarForm
        pending={create.isPending}
        error={create.error}
        onCancel={onClose}
        onSubmit={(body) => create.mutate(body, { onSuccess: onClose })}
      />
    </Dialog>
  );
}

export function EditCalendarDialog({
  calendar,
  onClose,
}: {
  calendar: Calendar;
  onClose: () => void;
}) {
  const update = useUpdateCalendar(calendar.id);
  const remove = useDeleteCalendar();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (confirmingDelete) {
    const inUse = errorCode(remove.error) === 'calendar_in_use';
    return (
      <Dialog open onClose={onClose} title="Excluir agenda">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir a agenda{' '}
          <strong className="text-foreground">{calendar.name}</strong>? Essa ação não pode ser
          desfeita.
        </p>
        {remove.error ? (
          <p className="mt-3 text-sm text-destructive">
            {inUse ? 'Há compromissos nesta agenda' : errorMessage(remove.error)}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDelete(false)}
          >
            Voltar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate(calendar.id, { onSuccess: onClose })}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title="Editar agenda">
      <CalendarForm
        initial={calendar}
        pending={update.isPending}
        error={update.error}
        onCancel={onClose}
        onSubmit={(body) => update.mutate(body, { onSuccess: onClose })}
        footerLeft={
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => setConfirmingDelete(true)}
          >
            Excluir
          </Button>
        }
      />
    </Dialog>
  );
}
